
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { ALLERGENS_LIST, DIETS_LIST } from '@/utils/constants';
import { toErrorMessage } from '@/utils/errorHandling';
import TextButton from '@/components/shared/suites/TextButton.vue';
import TextField from '@/components/shared/TextField.vue';
import ChipContainer from '@/components/shared/ChipContainer.vue';
import Toast from '@/utils/toast';

const namespace: string = 'rallyIntelligence';

@Component<TryPromptModal>({
	components: {
		TextButton,
		TextField,
		ChipContainer
	}
})
export default class TryPromptModal extends Vue {
	@Prop({ type: Number, default: null }) private promptId!: number | null;
	@Prop({ type: Object, default: null }) private selectedLocale!: RestaurantLocale | null;
	@Getter('getMonthlyTokensRemaining', { namespace }) private tokensRemaining!: number;
	@Getter('getPriceAffixes', { namespace: 'auth' }) private priceAffixes!: PriceAffixes;
	@Action('fetchUsage', { namespace }) private fetchUsage!: () => Promise<void>;
	@Action('tryPrompt', { namespace: 'cateringBot' }) private tryPrompt!: (args: { payload: CateringRecommendationRequest; locale?: string }) => Promise<CateringPackagesResponse | void>;

	private numberOfPeople: number = 25;
	private totalBudget: number = 1000;
	private selectedAllergens: string[] = [];
	private selectedDiets: string[] = [];
	private specialRequests: string = '';
	private running: boolean = false;

	private readonly allergenOptions = ALLERGENS_LIST.filter(a => a.value !== 'Alcohol');
	private readonly dietOptions = DIETS_LIST;

	private get canRun(): boolean {
		return Number.isFinite(this.numberOfPeople) && this.numberOfPeople >= 1 && Number.isFinite(this.totalBudget) && this.totalBudget >= 0;
	}

	/**
	 * Send the user's catering request to the server and emit the resulting
	 * packages so the parent can open the results modal.
	 *
	 * @return {Promise<void>}
	 */
	private async run(): Promise<void> {
		// Guard against invalid input (non-numeric guests/budget or out of range).
		if (!this.canRun) {
			return;
		}

		// Show the loading state on the "Run test" button.
		this.running = true;

		try {
			// Build the request body. Only the required fields (guests, budget)
			// are always sent — optional fields are attached only when the user
			// actually provided values for them.
			const payload: CateringRecommendationRequest = {
				guests: this.numberOfPeople,
				budget: this.totalBudget
			};
			if (this.promptId) {
				payload.prompt_id = this.promptId;
			}
			if (this.selectedDiets.length) {
				payload.dietary_preferences = [...this.selectedDiets];
			}
			if (this.selectedAllergens.length) {
				payload.allergens = [...this.selectedAllergens];
			}
			const trimmedSpecialRequests = this.specialRequests.trim();
			if (trimmedSpecialRequests) {
				payload.special_requests = trimmedSpecialRequests;
			}

			// Send the request, passing the selected locale so the server can
			// generate package names/descriptions in the right language.
			const response = await this.tryPrompt({
				payload,
				locale: this.selectedLocale?.locale_short
			});

			// Refresh the tokens-remaining counter shown at the bottom of the modal.
			// A failed refresh just leaves the stale counter — no toast needed.
			this.fetchUsage().catch(() => {});

			// If the server returned nothing usable, stop here — the parent stays
			// on this modal and the user can try again.
			if (!response) {
				return;
			}

			// Hand the results off to the parent so it can open the results modal.
			this.$emit('results', {
				numberOfPeople: this.numberOfPeople,
				totalBudget: this.totalBudget,
				packages: response.packages,
				dietaryPreferences: [...this.selectedDiets],
				allergens: [...this.selectedAllergens]
			});
		}
		catch (error) {
			Toast.error(this.$t('rally-intelligence.prompts.try.error_running', { errorMessage: toErrorMessage(error) }));
		}
		finally {
			this.running = false;
		}
	}
}
